/**
 * Performance-review guidance (Oct 2026): the short messages the tool shows at the right moment —
 * a first-visit intro, the peer-review anonymity note, the meeting-mode feedback primer, the
 * "same rating three times" reminder and the cycle banner. HR edits every text at Admin →
 * Performance reviews → Messages (`config.reviewGuidance`, partial; absent keys fall back to the
 * drafts below, so no migration). Seen state lives on the user doc (`reviewGuidanceSeen`).
 */
export const REVIEW_GUIDANCE_DEFAULTS = {
    intro: [
        {
            title: 'This review is for you',
            body: 'A performance review is not a form HR needs filled in. It is your moment to look at the year: what went well, what was hard, where you want to go next.\n\nSet aside real time for it, an hour without Slack, and write for yourself, not for the rating.',
        },
        {
            title: 'Do it yourself',
            body: 'AI is good at many things, but it cannot know how your year felt. Write your own answers in your own words. Short and honest beats long and polished.\n\nYour manager reads the texts, and the conversation builds on them.',
        },
        {
            title: 'Constructive, self-critical and proud',
            body: 'Top marks everywhere tell nobody anything, and neither does false modesty. Name what you are proud of, name what you would do differently, and say what you need.\n\nThat is what makes the conversation worth having.',
        },
    ],
    peer: [
        {
            title: 'Your feedback is anonymous',
            body: 'Your colleague never sees who wrote what. Only HR can, and only a combined picture reaches them.\n\nSo please make it count. "Great" on every line, or the middle everywhere, is the easy way and helps nobody. Think of one concrete moment per section: what they did well, what they could do differently. That is the feedback people remember.',
        },
    ],
    meeting: [
        {
            title: 'Giving feedback',
            body: 'Describe what you saw, not who you think the person is: a situation, what happened, what it led to. Start with what works. Be specific about what should change and say why it matters.\n\nAsk questions and listen to the answer. One or two points that land beat ten that do not.',
        },
        {
            title: 'Receiving feedback',
            body: 'Feedback is a gift: someone took the time to think about you. Receive it, do not defend it. Ask what they mean if it is unclear, say thank you, and take it away.\n\nThink about it over a few days. Then decide for yourself what you integrate and what you let go. Not everything will fit, but every piece deserves a real look.',
        },
    ],
    streak: {
        title: 'Same rating three times in a row',
        body: 'That can be right. But check: is each answer really the same, or is this the quick way through?\n\nA constructive, self-critical review rates each question on its own, and the comment is where the value is.',
    },
    banner: "It's review cycle time. Don't forget your surveys.",
};
/** Merge HR's edits over the drafts; empty lists and blank strings fall back too. */
export function resolveReviewGuidance(cfg) {
    const d = REVIEW_GUIDANCE_DEFAULTS;
    const screens = (v, fallback) => {
        const list = (v ?? []).filter((s) => s && (s.title?.trim() || s.body?.trim()));
        return list.length > 0 ? list : fallback;
    };
    return {
        intro: screens(cfg?.intro, d.intro),
        peer: screens(cfg?.peer, d.peer),
        meeting: screens(cfg?.meeting, d.meeting),
        streak: cfg?.streak && (cfg.streak.title?.trim() || cfg.streak.body?.trim()) ? cfg.streak : d.streak,
        banner: cfg?.banner?.trim() ? cfg.banner.trim() : d.banner,
    };
}
/** Key on `user.reviewGuidanceSeen`: the intro once per person, peer and meeting once per cycle. */
export function reviewGuidanceSeenKey(kind, cycleId) {
    return kind === 'intro' ? 'intro' : `${kind}:${cycleId ?? ''}`;
}
export const REVIEW_STREAK_LENGTH = 3;
/**
 * True when the rating question at `index` (survey order) has just been rated and the
 * `REVIEW_STREAK_LENGTH - 1` rated questions before it carry the same rating. Unrated
 * questions in between are skipped (the person may fill in any order).
 */
export function isRatingStreak(ratings, index) {
    const v = ratings[index];
    if (v == null)
        return false;
    let same = 1;
    for (let i = index - 1; i >= 0 && same < REVIEW_STREAK_LENGTH; i--) {
        const r = ratings[i];
        if (r == null)
            continue;
        if (r !== v)
            return false;
        same++;
    }
    return same >= REVIEW_STREAK_LENGTH;
}
//# sourceMappingURL=reviewGuidance.js.map